import { useEffect } from "react";

/**
 * Sets a `--vv-y` CSS custom property on <html> equal to the pixel translation
 * needed to keep a layout-centered element at the VISUAL viewport's center.
 *
 * Why this matters on iOS Safari:
 *   - Sections (`.portfolio-snap`) are sized at `100lvh` — the LARGEST viewport
 *     (URL-bar collapsed + tab-bar hidden). Their centered content sits at
 *     `lvh/2` from the layout top.
 *   - When iOS Safari expands its chrome (URL bar grows, tab bar reappears) —
 *     which happens at the end of every SPA route transition — the visible
 *     area shrinks. `lvh/2` (where the image sits) is no longer at the
 *     visible center: the image appears 40-60 px BELOW the visible center,
 *     perceived as a downward "jump" the moment the overlay fades out.
 *
 * The translation we need is:
 *   tY = vv.offsetTop + vv.height / 2 − layoutHeight / 2
 *
 * Apply `transform: translateY(var(--vv-y, 0px))` to any layout-centered
 * element (both the transition overlay and the destination HeroFrame use
 * this) and they will actively follow the visible center as iOS animates
 * its chrome — eliminating the jump.
 *
 * CRITICAL — iOS Safari specific:
 *   iOS Safari does NOT reliably fire `visualViewport.resize` during its
 *   own URL-bar / tab-bar animation (those events fire on pinch-zoom only).
 *   Listening only to vv events causes the translation to stay stale on
 *   Safari mobile while the chrome animates → the bug persists.
 *
 *   We compensate with:
 *     • `window.scroll` listener — fires on iOS Safari while URL bar
 *       animates in response to scroll.
 *     • `requestAnimationFrame` poll loop — once the transition overlay is
 *       active (`document.documentElement` carries the data-transitioning
 *       attribute), we poll every frame for ~1.5 seconds. This guarantees
 *       we catch every interpolation frame of the iOS chrome animation
 *       regardless of which (if any) DOM events Safari emits.
 */
export default function useVisualViewportCenter() {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;

    let lastTy = 0;
    const writeTy = () => {
      const layoutH = window.innerHeight;
      let ty = 0;
      if (vv) {
        ty = vv.offsetTop + vv.height / 2 - layoutH / 2;
      }
      // Only write when changed (avoid layout thrash on hot rAF loop).
      if (ty !== lastTy) {
        lastTy = ty;
        root.style.setProperty("--vv-y", `${ty}px`);
      }
    };

    writeTy();
    window.addEventListener("resize", writeTy);
    // iOS Safari fires window-level scroll while its URL bar animates.
    window.addEventListener("scroll", writeTy, { passive: true });
    if (vv) {
      vv.addEventListener("resize", writeTy);
      vv.addEventListener("scroll", writeTy);
    }

    // rAF poll loop — only runs while a route transition is in flight so it
    // costs nothing in steady state. The TransitionContext sets/unsets the
    // `data-transitioning` attribute on <html>.
    let rafId = 0;
    const poll = () => {
      writeTy();
      if (root.hasAttribute("data-transitioning")) {
        rafId = requestAnimationFrame(poll);
      } else {
        rafId = 0;
      }
    };
    const observer = new MutationObserver(() => {
      if (root.hasAttribute("data-transitioning") && !rafId) {
        rafId = requestAnimationFrame(poll);
      }
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-transitioning"]
    });

    return () => {
      window.removeEventListener("resize", writeTy);
      window.removeEventListener("scroll", writeTy);
      if (vv) {
        vv.removeEventListener("resize", writeTy);
        vv.removeEventListener("scroll", writeTy);
      }
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
}
